import { createEmitter } from "../Utilities/events";
import { MediaType, detectMediaType } from "../Core/MediaTypes";
import { StreamingProviderEvents } from "./StreamingProvider";
import {
  SurfaceKind,
  PlaybackProviderEvents,
} from "../Providers/Playback/PlaybackProviderContract";
import {
  extractVimeoId,
  extractVimeoPrivacyHash,
} from "../Providers/Playback/embedIds";
import { tpgTrace } from "../production/tpgPipelineTrace";

/**
 * Vimeo embed PlaybackProvider — iframe + postMessage player protocol.
 * Host chrome stays outside; Vimeo may still show mandated branding/controls
 * depending on account plan and embed privacy settings.
 *
 * Audio note: iframe `muted=1` sets an independent muted flag. Restoring
 * volume via setVolume alone does NOT unmute — must post setMuted(false).
 * YouTube uses mute()/unMute(); Vimeo must mirror that with setMuted.
 */
export function createVimeoEmbedProvider() {
  var emitter = createEmitter();
  var container = null;
  var iframe = null;
  var name = "vimeo-embed";
  var pollTimer = null;
  var readyTimer = null;
  var origin = "https://player.vimeo.com";
  var lastTime = 0;
  var lastDuration = 0;
  var lastMuted = true;
  var lastVolume = 1;
  var playing = false;
  var playerReady = false;
  var onMessage = null;
  var loadResolve = null;
  var playRequested = false;
  var playWaitTimer = null;
  var playWaitResolve = null;
  var playWaitReject = null;

  function post(method, value) {
    if (!iframe || !iframe.contentWindow) {
      tpgTrace("STATE", {
        message: "vimeo_post_skipped_no_iframe",
        method: method,
        value: value,
        playerReady: playerReady,
      });
      return false;
    }
    var msg = { method: method, player_id: "ngp_vimeo" };
    if (value !== undefined) msg.value = value;
    iframe.contentWindow.postMessage(JSON.stringify(msg), origin);
    return true;
  }

  /**
   * Apply engine mute/volume to the iframe. Safe to call before ready —
   * commands are re-applied when the ready event arrives.
   */
  function applyAudioToPlayer(reason) {
    var postedMuted = post("setMuted", !!lastMuted);
    var postedVol = false;
    // Always push volume; when unmuted, restore lastVolume so muted=1 loads
    // do not stay silent after setMuted(false).
    if (lastMuted) {
      postedVol = post("setVolume", 0);
    } else {
      postedVol = post("setVolume", lastVolume);
    }
    tpgTrace(lastMuted ? "PROVIDER_MUTED" : "PROVIDER_UNMUTED", {
      provider: name,
      reason: reason || "applyAudioToPlayer",
      lastMuted: lastMuted,
      lastVolume: lastVolume,
      playerReady: playerReady,
      postedMuted: postedMuted,
      postedVol: postedVol,
      method: "setMuted+setVolume",
    });
    tpgTrace("PROVIDER_VOLUME", {
      provider: name,
      muted: lastMuted,
      volume: lastVolume,
      playerReady: playerReady,
      reason: reason || "applyAudioToPlayer",
    });
    return postedMuted || postedVol;
  }

  function stopPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function clearReadyTimer() {
    if (readyTimer) {
      clearTimeout(readyTimer);
      readyTimer = null;
    }
  }

  function clearPlayWaitTimer() {
    if (playWaitTimer) {
      clearTimeout(playWaitTimer);
      playWaitTimer = null;
    }
  }

  function settlePlayWait(ok, err) {
    var resolve = playWaitResolve;
    var reject = playWaitReject;
    playWaitResolve = null;
    playWaitReject = null;
    clearPlayWaitTimer();
    if (ok) {
      if (typeof resolve === "function") resolve();
    } else if (typeof reject === "function") {
      reject(err || new Error("vimeo autoplay blocked"));
    }
  }

  function markPlaying() {
    playing = true;
    settlePlayWait(true);
  }

  /**
   * Wait for actual playback (play event, getPaused=false, or time advancing).
   * Must not start this clock until the iframe is ready — intro remount often
   * calls play() before Vimeo's ready event (~2s), which used to reject as
   * "autoplay blocked" while the player was still loading.
   */
  function watchForPlayback() {
    clearPlayWaitTimer();
    var attempts = 0;
    var check = function () {
      attempts += 1;
      if (playing) {
        settlePlayWait(true);
        return;
      }
      post("getPaused");
      post("getCurrentTime");
      if (attempts >= 10) {
        tpgTrace("STATE", {
          message: "vimeo_autoplay_blocked",
          muted: lastMuted,
          playerReady: playerReady,
        });
        settlePlayWait(false, new Error("vimeo autoplay blocked"));
        return;
      }
      playWaitTimer = setTimeout(check, 150);
    };
    playWaitTimer = setTimeout(check, 150);
  }

  function startPoll() {
    stopPoll();
    pollTimer = setInterval(function () {
      post("getCurrentTime");
      post("getDuration");
    }, 400);
  }

  function handleMessage(event) {
    if (!event || event.origin.indexOf("vimeo.com") === -1) return;
    var data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    if (!data) return;
    if (data.event === "ready") {
      playerReady = true;
      post("addEventListener", "play");
      post("addEventListener", "pause");
      post("addEventListener", "finish");
      post("addEventListener", "seek");
      post("addEventListener", "playProgress");
      post("addEventListener", "error");
      // Re-apply audio after ready — early setMuted/setVolume posts are ignored.
      applyAudioToPlayer("vimeo_ready_reapply");
      tpgTrace("PROVIDER_READY", {
        provider: name,
        lastMuted: lastMuted,
        lastVolume: lastVolume,
      });
      emitter.emit(PlaybackProviderEvents.READY);
      emitter.emit(StreamingProviderEvents.MANIFEST_PARSED, {
        levels: [],
        currentLevel: -1,
      });
      if (typeof loadResolve === "function") {
        var resolve = loadResolve;
        loadResolve = null;
        clearReadyTimer();
        resolve();
      }
      post("getPaused");
      // Intro→main remount can request play before ready; start watch only now.
      if (playRequested && !playing) {
        post("play");
        watchForPlayback();
      }
    }
    if (data.event === "play") {
      markPlaying();
      tpgTrace("PROVIDER_PLAY", { provider: name, muted: lastMuted });
      tpgTrace("FIRST_FRAME", { provider: name, surface: "iframe" });
      emitter.emit(PlaybackProviderEvents.PLAY);
    }
    if (data.event === "playProgress") {
      var seconds =
        data.data && data.data.seconds != null
          ? Number(data.data.seconds)
          : 0;
      if (seconds > 0.05) {
        markPlaying();
      }
    }
    if (data.event === "error") {
      tpgTrace("ERROR", {
        message: "vimeo_player_error",
        data: data.data || data,
      });
      settlePlayWait(false, new Error("vimeo player error"));
    }
    if (data.method === "getPaused" && (data.value === false || data.value === 0)) {
      markPlaying();
    }
    if (data.event === "pause") {
      playing = false;
      emitter.emit(PlaybackProviderEvents.PAUSE);
    }
    if (data.event === "finish") {
      playing = false;
      emitter.emit(PlaybackProviderEvents.ENDED);
    }
    if (data.event === "seek") {
      emitter.emit(PlaybackProviderEvents.SEEK, data.data || {});
    }
    if (data.method === "getCurrentTime" && data.value != null) {
      lastTime = Number(data.value) || 0;
      if (playRequested && lastTime > 0.05) {
        markPlaying();
      }
      emitter.emit(PlaybackProviderEvents.TIME, {
        currentTime: lastTime,
        duration: lastDuration,
      });
      emitter.emit(PlaybackProviderEvents.PROGRESS, {
        currentTime: lastTime,
        duration: lastDuration,
      });
    }
    if (data.method === "getDuration" && data.value != null) {
      lastDuration = Number(data.value) || 0;
    }
  }

  return {
    getName: function () {
      return name;
    },
    getSurfaceKind: function () {
      return SurfaceKind.EMBED;
    },
    initialize: function () {
      return Promise.resolve();
    },
    supports: function (source) {
      if (!source || !source.src) return false;
      var type = source.type || detectMediaType(source.src);
      return type === MediaType.VIMEO || !!extractVimeoId(source.src);
    },
    attach: function (el) {
      container = el;
      if (container) container.innerHTML = "";
      if (typeof window !== "undefined") {
        onMessage = handleMessage;
        window.addEventListener("message", onMessage);
      }
    },
    detach: function () {
      stopPoll();
      clearReadyTimer();
      settlePlayWait(false, new Error("vimeo detached"));
      loadResolve = null;
      playerReady = false;
      playRequested = false;
      playing = false;
      if (onMessage && typeof window !== "undefined") {
        window.removeEventListener("message", onMessage);
      }
      onMessage = null;
      iframe = null;
      if (container) container.innerHTML = "";
      container = null;
    },
    load: function (source, options) {
      options = options || {};
      var id = extractVimeoId(source && source.src);
      if (!id) {
        return Promise.reject({
          code: "VIMEO_ID",
          message: "Could not parse Vimeo id",
          recoverable: false,
        });
      }
      if (!container) {
        return Promise.reject(new Error("vimeo-embed: not attached"));
      }
      lastMuted = options.muted != null ? !!options.muted : true;
      lastVolume =
        options.volume != null
          ? Math.max(0, Math.min(1, Number(options.volume) || 1))
          : lastVolume;
      playerReady = false;
      playRequested = false;
      playing = false;
      settlePlayWait(false, new Error("vimeo reloaded"));
      clearReadyTimer();
      var privacyHash = extractVimeoPrivacyHash(source && source.src);
      tpgTrace("MAIN_PROVIDER_CREATED", {
        provider: name,
        id: id,
        muted: lastMuted,
        volume: lastVolume,
      });
      if (lastMuted) {
        tpgTrace("PROVIDER_MUTED", {
          provider: name,
          reason: "load_url_muted_param",
          lastMuted: true,
        });
      }
      container.innerHTML = "";
      iframe = document.createElement("iframe");
      iframe.className = "ngp-embed-frame ngp-embed-frame--vimeo";
      iframe.id = "ngp_vimeo";
      iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
      iframe.setAttribute("allowfullscreen", "true");
      iframe.title = "Vimeo video";
      // controls=0 requests host chrome; Vimeo may ignore on free/basic embeds.
      // autoplay=1 is required for Vimeo's iframe autoplay; mute-retry still
      // depends on play() rejecting when the browser blocks unmuted start.
      var q =
        "https://player.vimeo.com/video/" +
        id +
        "?api=1&player_id=ngp_vimeo&autoplay=1&playsinline=1&title=0&byline=0&portrait=0&controls=0" +
        (lastMuted ? "&muted=1" : "") +
        (privacyHash ? "&h=" + encodeURIComponent(privacyHash) : "");
      iframe.src = q;
      container.appendChild(iframe);
      startPoll();
      return new Promise(function (resolve) {
        loadResolve = resolve;
        // Fallback if ready event is delayed/missed — still resolve so play() runs
        readyTimer = setTimeout(function () {
          readyTimer = null;
          if (typeof loadResolve === "function") {
            tpgTrace("STATE", {
              message: "vimeo_ready_timeout_fallback",
              playerReady: playerReady,
              lastMuted: lastMuted,
            });
            // Best-effort re-apply even if ready was missed
            applyAudioToPlayer("vimeo_ready_timeout");
            var r = loadResolve;
            loadResolve = null;
            r();
          }
        }, 2500);
      });
    },
    play: function () {
      playRequested = true;
      tpgTrace("PROVIDER_PLAY", {
        provider: name,
        via: "play()",
        muted: lastMuted,
        playerReady: playerReady,
      });
      if (playing) {
        return Promise.resolve();
      }
      return new Promise(function (resolve, reject) {
        playWaitResolve = resolve;
        playWaitReject = reject;
        if (!playerReady) {
          // Do not treat a pre-ready timeout as autoplay-blocked. Ready will
          // post play and start watchForPlayback. Cap the wait so mute-retry
          // can still run if the iframe never becomes ready.
          clearPlayWaitTimer();
          playWaitTimer = setTimeout(function () {
            if (playing) {
              settlePlayWait(true);
              return;
            }
            if (!playerReady) {
              tpgTrace("STATE", {
                message: "vimeo_autoplay_blocked",
                muted: lastMuted,
                playerReady: false,
              });
              settlePlayWait(false, new Error("vimeo autoplay blocked"));
            }
          }, 8000);
          return;
        }
        post("play");
        watchForPlayback();
      });
    },
    pause: function () {
      post("pause");
      playing = false;
    },
    seek: function (t) {
      if (isFinite(t)) {
        post("setCurrentTime", t);
        lastTime = t;
      }
    },
    setMuted: function (m) {
      lastMuted = !!m;
      applyAudioToPlayer("engine_setMuted");
      emitter.emit(PlaybackProviderEvents.VOLUME, {
        muted: lastMuted,
        volume: lastVolume,
      });
    },
    setVolume: function (v) {
      lastVolume = Math.max(0, Math.min(1, v));
      if (!lastMuted) {
        post("setVolume", lastVolume);
      }
      tpgTrace("PROVIDER_VOLUME", {
        provider: name,
        volume: lastVolume,
        muted: lastMuted,
        playerReady: playerReady,
        reason: "engine_setVolume",
      });
      emitter.emit(PlaybackProviderEvents.VOLUME, {
        muted: lastMuted,
        volume: lastVolume,
      });
    },
    getCurrentTime: function () {
      return lastTime;
    },
    getDuration: function () {
      return lastDuration;
    },
    isPlaying: function () {
      return playing;
    },
    isMuted: function () {
      return lastMuted;
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      this.detach();
      emitter.emit(PlaybackProviderEvents.DESTROYED);
      emitter.emit(StreamingProviderEvents.DESTROYED);
      emitter.removeAllListeners();
    },
  };
}

export default createVimeoEmbedProvider;
